class FreeGiftModal extends HTMLElement {
  constructor() {
    super();
    this.dialog = this.querySelector('[role="dialog"]');
    this.error = this.querySelector('[data-free-gift-error]');
    this.onKeyUp = this.onKeyUp.bind(this);

    /*
     * A dismissing click must not leak to document.body: the cart notification opens in a
     * microtask while this very click is still bubbling, and its body listener would treat
     * it as a click-outside and close the notification immediately.
     */
    this.addEventListener('click', (event) => event.stopPropagation());

    this.querySelectorAll('[data-free-gift-close]').forEach((button) => {
      button.addEventListener('click', () => this.close());
    });

    this.querySelectorAll('[data-free-gift-variant]').forEach((button) => {
      button.addEventListener('click', () => {
        this.close(Number(button.dataset.freeGiftVariant));
      });
    });
  }

  hasAvailableVariant() {
    return Boolean(this.querySelector('[data-free-gift-variant]:not([disabled])'));
  }

  // Resolves with a variant id, or null when the customer declines.
  choose(opener) {
    if (this.pendingChoice) return this.pendingChoice;

    const availableButton = this.querySelector('[data-free-gift-variant]:not([disabled])');
    if (!availableButton) return Promise.resolve(null);

    this.opener = opener || document.activeElement;
    this.error.hidden = true;
    this.setAttribute('open', '');
    this.setAttribute('aria-hidden', 'false');
    document.body.classList.add('free-gift-modal-open');
    document.addEventListener('keyup', this.onKeyUp);

    this.pendingChoice = new Promise((resolve) => {
      this.resolveChoice = resolve;
    });

    requestAnimationFrame(() => availableButton.focus());
    return this.pendingChoice;
  }

  close(variantId = null) {
    if (!this.hasAttribute('open')) return;

    this.removeAttribute('open');
    this.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('free-gift-modal-open');
    document.removeEventListener('keyup', this.onKeyUp);

    if (this.resolveChoice) this.resolveChoice(variantId);
    this.pendingChoice = null;
    this.resolveChoice = null;

    if (this.opener && typeof this.opener.focus === 'function') this.opener.focus();
  }

  onKeyUp(event) {
    if (event.code === 'Escape') this.close();
  }
}

if (!customElements.get('free-gift-modal')) {
  customElements.define('free-gift-modal', FreeGiftModal);
}

class FreeGiftManager {
  constructor(config) {
    this.config = config;
    this.strings = config.strings;
    this.productId = Number(config.productId);
    this.maxQuantity = Math.max(1, Number(config.maxQuantity) || 1);
    this.variantIds = new Set((config.variantIds || []).map(Number));
    this.modal = document.getElementById('FreeGiftModal');
    this.addQueue = Promise.resolve();
  }

  isActive() {
    return Boolean(this.productId);
  }

  // Serialises add-to-cart runs so a double click cannot queue two gifts.
  async acquireAddLock() {
    let releaseLock;
    const previousAdd = this.addQueue;
    this.addQueue = new Promise((resolve) => {
      releaseLock = resolve;
    });
    await previousAdd;
    return releaseLock;
  }

  isGiftVariantId(variantId) {
    return this.variantIds.has(Number(variantId));
  }

  isGiftLine(item) {
    return Number(item.product_id) === this.productId || item.properties?._free_gift === 'true';
  }

  /*
   * One gift per qualifying unit in the cart, never more than the configured maximum, so a
   * single-item cart earns one gift and a multi-item cart earns up to the cap.
   * `extraQualifyingUnits` covers units added after the cart snapshot was taken.
   */
  giftAllowance(cart, extraQualifyingUnits = 0) {
    const qualifyingUnits = cart.items
      .filter((item) => !this.isGiftLine(item))
      .reduce((total, item) => total + item.quantity, extraQualifyingUnits);

    return Math.min(this.maxQuantity, qualifyingUnits);
  }

  claimedGifts(cart) {
    return cart.items
      .filter((item) => this.isGiftLine(item))
      .reduce((total, item) => total + item.quantity, 0);
  }

  // Returns an error message when the submitted form is the gift itself, else null.
  rejectAdd(formData) {
    if (!this.isActive()) return null;
    return this.isGiftVariantId(formData.get('id')) ? this.strings.blocked : null;
  }

  async getCart() {
    const response = await fetch(`${window.routes.cart_url}.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) throw new Error('Unable to load cart.');
    return response.json();
  }

  /*
   * Starts the cart lookup so it can run alongside the add-to-cart request instead of
   * after it — adding a non-gift product cannot change whether a gift is already there.
   */
  primeCart() {
    if (!this.isActive() || !this.modal) return;
    this.cartPromise = this.getCart();
    this.cartPromise.catch(() => {});
  }

  consumeCart() {
    const pending = this.cartPromise || this.getCart();
    this.cartPromise = null;
    return pending;
  }

  /*
   * Runs after the customer's own product is already in the cart, so it must never
   * throw: a failed or declined gift still leaves a completed add-to-cart.
   * Offers a single gift per add-to-cart, so a shopper picks each size on its own.
   * Resolves with { response } when a gift was added, { error } when it could not be.
   */
  async offerGift(opener, sections, sectionsUrl, addedQuantity = 1) {
    if (!this.isActive() || !this.modal) {
      this.cartPromise = null;
      return {};
    }

    try {
      /*
       * The primed cart was read alongside the add, so it predates it. The gift itself can
       * never be submitted, so the units just added always count towards the allowance.
       */
      const cart = await this.consumeCart();
      const allowance = this.giftAllowance(cart, addedQuantity);
      const claimed = this.claimedGifts(cart);

      if (claimed >= allowance) {
        if (claimed > allowance) await this.normalizeGiftLines(cart, allowance);
        return {};
      }

      /*
       * Nothing purchasable to offer. The shopper never asked for a gift and their own
       * product is already in the cart, so this stays silent for them and loud for us.
       */
      if (!this.modal.hasAvailableVariant()) {
        const total = this.modal.querySelectorAll('[data-free-gift-variant]').length;
        console.warn(
          `[free-gift] Skipped: the gift product has no purchasable variant (${total} found, all unavailable). ` +
          'Check its inventory in Shopify Admin, or enable "Continue selling when out of stock".'
        );
        return {};
      }

      const variantId = await this.modal.choose(opener);
      if (!variantId) return {};

      const response = await this.addGift(variantId, sections, sectionsUrl);
      return response.status ? { error: this.strings.addError } : { response, variantId };
    } catch (error) {
      console.error(error);
      return { error: this.strings.addError };
    }
  }

  async addGift(variantId, sections, sectionsUrl) {
    const formData = new FormData();
    formData.append('id', String(variantId));
    formData.append('quantity', '1');
    formData.append('properties[_free_gift]', 'true');
    formData.append('sections', sections.join(','));
    formData.append('sections_url', sectionsUrl);

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    config.body = formData;

    const response = await fetch(window.routes.cart_add_url, config);
    return response.json();
  }

  // Trims the gift lines back to the allowance, keeping the sizes picked first.
  async normalizeGiftLines(cart, allowance) {
    const giftLines = cart.items.filter((item) => this.isGiftLine(item));
    if (giftLines.length === 0) return;

    let remaining = Math.max(0, allowance);
    const updates = {};
    giftLines.forEach((item) => {
      const keep = Math.min(item.quantity, remaining);
      remaining -= keep;
      updates[item.key] = keep;
    });

    await fetch(window.routes.cart_update_url, {
      ...fetchConfig(),
      body: JSON.stringify({ updates }),
    });
  }

  /*
   * How many gifts the rendered cart page has earned but not yet claimed. Reads the DOM the
   * cart response just painted, so a quantity change costs no extra request.
   */
  cartPageShortfall() {
    const cartItems = document.querySelector('cart-items');
    if (!cartItems) return 0;

    const rows = Array.from(cartItems.querySelectorAll('[data-cart-item-key]'));
    const units = (isGift) => rows
      .filter((row) => (row.dataset.freeGift === 'true') === isGift)
      .reduce((total, row) => total + this.getRowQuantity(row), 0);

    return Math.min(this.maxQuantity, units(false)) - units(true);
  }

  /*
   * A cart-page quantity change raises the allowance too — a second item earns a second gift,
   * and a gift always needs a size. Never throws: the quantity change is already applied.
   */
  async offerGiftOnCartPage(cartItems) {
    if (!this.isActive() || !this.modal || this.cartOfferPending || this.cartOfferDeclined) return;
    if (this.cartPageShortfall() < 1 || !this.modal.hasAvailableVariant()) return;

    this.cartOfferPending = true;

    try {
      const variantId = await this.modal.choose();

      // Nudging quantities around must not reopen an offer the shopper has already turned down.
      if (!variantId) {
        this.cartOfferDeclined = true;
        return;
      }

      const sections = cartItems.getSectionsToRender().map((section) => section.section);
      const response = await this.addGift(variantId, sections, window.location.pathname);

      if (response.status) throw new Error(response.description || 'Unable to add the free gift.');
      cartItems.renderSections(response);
    } catch (error) {
      console.error(error);
    } finally {
      this.cartOfferPending = false;
    }
  }

  getRowQuantity(row) {
    const input = row.querySelector('[name="updates[]"]');
    return input ? Math.max(0, parseInt(input.value, 10) || 0) : 0;
  }

  /*
   * Rewrites a cart-page quantity change into a single /cart/update.js call that also
   * settles the gift line. Reads the rendered cart rather than refetching it, so there
   * is no extra round trip and no reliance on positional line indexes.
   * Returns null to let the caller fall back to the stock /cart/change.js request.
   */
  getCartUpdateRequest(line, quantity, sections, sectionsUrl) {
    if (!this.isActive()) return null;

    const cartItems = document.querySelector('cart-items');
    const changedRow = document.getElementById(`CartItem-${line}`);
    if (!cartItems || !changedRow || !changedRow.dataset.cartItemKey) return null;

    // Changing the gift line itself is an ordinary line change.
    if (changedRow.dataset.freeGift === 'true') return null;

    const rows = Array.from(cartItems.querySelectorAll('[data-cart-item-key]'));
    const giftRows = rows.filter((row) => row.dataset.freeGift === 'true');
    if (giftRows.length === 0) return null;

    const nextQuantity = Math.max(0, parseInt(quantity, 10) || 0);
    const updates = { [changedRow.dataset.cartItemKey]: nextQuantity };

    const qualifyingQuantity = rows
      .filter((row) => row.dataset.freeGift !== 'true')
      .reduce((total, row) => total + (row === changedRow ? nextQuantity : this.getRowQuantity(row)), 0);

    /*
     * This request only ever takes gifts away — an extra one needs a size, so it is offered
     * by offerGiftOnCartPage once the change has been applied.
     */
    let remaining = Math.min(this.maxQuantity, qualifyingQuantity);
    giftRows.forEach((row) => {
      const keep = Math.min(this.getRowQuantity(row), remaining);
      remaining -= keep;
      updates[row.dataset.cartItemKey] = keep;
    });

    return {
      url: window.routes.cart_update_url,
      body: JSON.stringify({
        updates,
        sections,
        sections_url: sectionsUrl,
      }),
    };
  }
}

if (window.freeGiftConfig) {
  window.freeGift = new FreeGiftManager(window.freeGiftConfig);
}
